import { useRef, useEffect } from 'react';
import { useCallbackOne } from 'use-memo-one';
import {
	BridgeContextObject,
	type BridgeContextInput,
	type ChangeListener,
} from './BridgeContextObject';
import type { BridgeContext, BridgeServices, BridgeControls } from './types';
import { emit } from '../custom-ui/bridge/methods/events';
import stringify from 'fast-json-stable-stringify';

export const useBridgeContext = (
	props: BridgeContextInput,
	getServices: () => BridgeServices,
): {
	destroy: () => void;
	getBridgeContext: () => BridgeContext;
	onChange: (listener: ChangeListener) => () => void;
	setBridgeControl: (controls: BridgeControls) => void;
} => {
	// Created once for the lifetime of the component.
	// BridgeContextObject owns all memoization and change notification internally.
	const contextObjectRef = useRef<BridgeContextObject | null>(null);
	if (contextObjectRef.current === null) {
		contextObjectRef.current = new BridgeContextObject(props, getServices);
	}

	// Update after every commit. Bridge calls arrive via postRobot (async message events)
	// so the context will always be current before any bridge method is invoked.
	useEffect(() => {
		contextObjectRef.current!.update(props);
	});

	const getBridgeContext = useCallbackOne(() => contextObjectRef.current!.get(), []);
	const destroy = useCallbackOne(() => contextObjectRef.current!.destroy(), []);
	const onChange = useCallbackOne(
		(listener: ChangeListener) => contextObjectRef.current!.onChange(listener),
		[],
	);
	const setBridgeControl = useCallbackOne(
		(controls: BridgeControls) => contextObjectRef.current!.setBridgeControl(controls),
		[],
	);

	// This registers general bridge context change events that is common to all
	// bridge runtimes and Forge modules.
	useEffect(() => {
		return onChange((prev, next) => {
			if (prev.theme !== next.theme || prev.surfaceColor !== next.surfaceColor) {
				emit('FORGE_CORE_THEME_CHANGED', { theme: next.theme });
			}
			if (prev.extension !== next.extension) {
				// `config` is a Confluence-specific runtime field not present in the GQLExtension type.
				const prevConfig = (prev.extension as { config?: unknown } | undefined)?.config;
				const nextConfig = (next.extension as { config?: unknown } | undefined)?.config;

				// TODO: FORGE_CORE_MACRO_CONFIG_CHANGED is a Confluence-specific concept (macro config)
				// and does not belong in the forge-ui framework layer. Once product-level bridge
				// listener registration exists, Confluence should register this listener from its own
				// forge integration layer and this emit should be removed here.
				if (stringify(prevConfig) !== stringify(nextConfig)) {
					emit('FORGE_CORE_MACRO_CONFIG_CHANGED', { config: nextConfig });
				}
			}
		});
	}, [onChange]);

	return { getBridgeContext, destroy, onChange, setBridgeControl };
};
